'use client';
import { Button } from '@mui/material';

import { CalendarIcon, useTranslation } from '@abg/kit-base';
import { plainDtos } from '@abg/shared';
import { usePageContext } from '@abg/web-core/context/page/client-provider';

import { TConfirmationI18n } from '../../organisms/Confirmation';
import { useAnalyticsGeneralEngagement } from '../../shared/analytics/hooks/use-analytics-general-engagement';
import ics from '../../shared/utils/add-to-calendar-ics';

import { actionButtonStyle } from './AddToCalendarCta.styling';
import { formatDateTime } from './utils';

const handleAddToCalendar = ({
  details,
  name,
  confirmationNumber,
  pickUpDateTime,
  dropOffDateTime,
  pickUpUserAddress,
}: {
  details: string;
  name: string;
  confirmationNumber: string;
  pickUpDateTime: string;
  dropOffDateTime: string;
  pickUpUserAddress: string;
}) => {
  const cal = ics();
  cal?.addEvent(
    name,
    details,
    pickUpUserAddress,
    pickUpDateTime,
    dropOffDateTime,
  );
  cal?.download(confirmationNumber, '.ics');
};

const AddToCalendarCTA = ({
  bookingDetails,
  i18n,
  modifyBookingUrl,
}: {
  bookingDetails: plainDtos.DisplayReservationResponse | null;
  i18n: TConfirmationI18n;
  modifyBookingUrl?: string;
}) => {
  const t = useTranslation(i18n);
  const { brand } = usePageContext();
  const { emitEvent: emitGeneralEngagementEvent } =
    useAnalyticsGeneralEngagement();

  if (!bookingDetails) {
    return;
  }
  const {
    confirmationNumber,
    pickUpAddresses,
    dropOffAddresses,
    pickUpDateTime,
    dropOffDateTime,
    customerInformation,
    products,
  } = bookingDetails;

  const name = `${brand.charAt(0).toUpperCase() + brand.slice(1)} Rent A Car Reservation Confirmation Number : ${confirmationNumber}`;
  const singleLineSpacer = '=0A';
  const doubleLineSpacer = '=0A=0A';
  let details = singleLineSpacer;

  // Todo: All Keys to be authorable
  // Todo: Replace mocks with API data
  // Reservation Confirmation Number
  details += `Reservation Confirmation Number: ${confirmationNumber} ${doubleLineSpacer}`;
  if (modifyBookingUrl) {
    details += `${t(
      'reservationConfirmation.bookingDetails.modifyBookingCTA.label',
    )}: ${modifyBookingUrl} ${doubleLineSpacer}`;
  }

  // Rent for
  details += `This rent is for: ${customerInformation?.firstName} ${customerInformation?.lastName} ${doubleLineSpacer}`;

  // Pick up Location
  const pickUpAddress = pickUpAddresses?.[0];
  const userPickUpAddress = `${pickUpAddress?.street}, ${pickUpAddress?.city}, ${pickUpAddress?.state}`;
  details += `${t(
    'reservationConfirmation.bookingDetails.pickUpLocation.label',
  )}: ${userPickUpAddress}${doubleLineSpacer}`;

  // Pick up Date and time
  const {
    fullDateTime: pickUpFullDateTime,
    formattedDate: pickUpFormattedDate,
    formattedTime: pickUpFormattedTime,
  } = formatDateTime(pickUpDateTime.date, pickUpDateTime.time);
  details += `Pick-up Date: ${pickUpFormattedDate} ${doubleLineSpacer}`;

  // Pick up Time
  details += `Pick-up Time: ${pickUpFormattedTime} ${doubleLineSpacer}`;

  // Drop off Location
  const dropOffAddress = dropOffAddresses?.[0];
  const userDropOffAddress = `${dropOffAddress?.street}, ${dropOffAddress?.city}, ${dropOffAddress?.state}`;
  details += `${t(
    'reservationConfirmation.bookingDetails.dropOffLocation.label',
  )}: ${userDropOffAddress}${doubleLineSpacer}`;

  // Return Date
  const {
    fullDateTime: dropOffFullDateTime,
    formattedDate: dropOffFormattedDate,
    formattedTime: dropOffFormattedTime,
  } = formatDateTime(dropOffDateTime.date, dropOffDateTime.time);
  details += `Return Date: ${dropOffFormattedDate} ${doubleLineSpacer}`;

  // Return Time
  // const formattedReturnTime = format(returnDate, 'hh:mm a');
  details += `Return Time: ${dropOffFormattedTime} ${doubleLineSpacer}`;

  // Car Type
  details += `Car Class: ${products?.[0]?.carGroupName} ${doubleLineSpacer}`;

  // Contact Number
  details += `Contact us at anytime: United States - 1-800-230-4898 // Argentina - 0810-9991-2840 // Australia - 1300 137 498 // Brazil - (55-11) 4225-8456 // Canada - 1-800-879-2847 // Mexico - 01-800-288-888 // New Zealand - 09-526-2847 // Puerto Rico - 787-253-5925 // US Virgin Islands - 340-778-9355 ${doubleLineSpacer}`;

  return (
    <Button
      variant="outlined"
      className="secondary"
      sx={actionButtonStyle}
      data-testid="reservation-details-addToCalendar-button"
      onClick={(event) => {
        emitGeneralEngagementEvent(event.currentTarget);
        handleAddToCalendar({
          details,
          name,
          confirmationNumber,
          pickUpDateTime: pickUpFullDateTime,
          dropOffDateTime: dropOffFullDateTime,
          pickUpUserAddress: userPickUpAddress,
        });
      }}
    >
      {t('reservationConfirmation.bookingDetails.addToCalendarCTA.label')}
      <CalendarIcon />
    </Button>
  );
};

export default AddToCalendarCTA;
